import React, { useEffect, useState } from 'react';
import { PollApi, PollCampaignApi, PollResponseApi } from './api';
import { FlatList, Modal, useWindowDimensions, Alert } from 'react-native';
import type { BEEventProps, PollCampaignGoalProps, PollCampaignProps, PollOptionProps, PollProps, PollResponseProps } from '../types';
import CampaignHeader from './components/CampaignHeader';
import CampaignProgressBar from './components/CampaignProgressBar';
import PollQuestionsSection from './components/PollQuestionsSection';
import CampaignResult from './components/CampaignResult';
import CampaignLeaderboard from './components/CampaignLeaderboard';
import PollCard from './components/PollCard';
import SocketManager from '../Socket';
import { View } from '../Components/Themed';

type PollCampaignModuleProps = {
    poll_campaign_id:string,
    poll_id?:string,
    player_id?:string,
    view_mode:'desktop'|'mobile',
    distinct_id:string,
    onFlashSelect?:(poll_id:string) => void,
    insets?:{ top:number, bottom:number, left:number, right:number },
    onBack?:() => void,
    onEvent?:(be_event:BEEventProps) => void,
    onRequestAuthenticate: (auth_strategy_id?:string, company_id?:string) => void,
    onSharePollCampaign: (poll_campaign:PollCampaignProps) => void,
    onComplete:() => void

}

const sections = ['header', 'progress', 'results','questions']
const PollCampaign = ({ distinct_id, view_mode, poll_campaign_id, player_id, insets, onBack, onFlashSelect, onRequestAuthenticate, onSharePollCampaign }: PollCampaignModuleProps ) => {
    const [ needs_reload, setNeedsReload ] = useState(false);
    const [ socket_polls, setSocketPolls ] = useState<PollProps[]>([]);
    const [ socket_campaign, setSocketCampaign ] = useState<PollCampaignProps|undefined>(undefined);
    const [ module_data, setModuleData ] = useState<{
        loading: boolean,
        active_poll_id?:string,
        poll_campaign?:PollCampaignProps,
        polls:PollProps[],
        poll_campaign_goals:PollCampaignGoalProps[]
    }>({
        loading: false,
        polls: [],
        poll_campaign_goals:[]
    });
    const [ full_leaderboard, setFullLeaderboard ] = useState(false);
    const [ my_data, setMyData ] = useState<{
        action_loading:boolean,
        poll_responses:PollResponseProps[]
    }>({
        action_loading: false,
        poll_responses: []
    });
    const { poll_responses, action_loading } = my_data;
    const { loading, poll_campaign, polls, active_poll_id } = module_data;
    const active_response = poll_responses.find(r => r.poll_id == active_poll_id);
    const active_polls = polls.filter(p => !['inactive'].includes(p.status)).sort((a,b) => a.priority - b.priority);
    const { width, height } = useWindowDimensions();
    useEffect(() => {
        PollCampaignApi.setEnvironment();
        getDataFromServer();
    },[poll_campaign_id, player_id])

    useEffect(() => {
        if(socket_polls.length == 0){ return }
        setModuleData({
            ...module_data,
            polls: polls.filter(p => !socket_polls.find(sp => sp.poll_id == p.poll_id)).concat(socket_polls)
        })
    },[socket_polls])

    useEffect(() => {
        if(!socket_campaign){ return }
        setModuleData({
            ...module_data,
            poll_campaign: socket_campaign
        })
    },[socket_campaign]);

    const getDataFromServer = async() => {
        setModuleData({ ...module_data, loading:true });
        const pc = await PollCampaignApi.getPollCampaignById(poll_campaign_id);
        const ps_resp = await PollApi.getPollsByCampaignId(poll_campaign_id);
        if(player_id){
            let my_responses = await PollResponseApi.getMyCampaignResponses(poll_campaign_id);
            setMyData({
                ...my_data,
                poll_responses: my_responses
            })
        }
        setModuleData({
            ...module_data,
            poll_campaign: pc,
            polls: ps_resp.polls,
            poll_campaign_goals: ps_resp.poll_campaign_goals, 
            loading:false
        })
    }

    const handleSubmitResponse = async(pr:PollResponseProps) => {
        if(!player_id){ return onRequestAuthenticate(poll_campaign?.auth_strategy_id, poll_campaign?.company_id) }
        try {
            if(!pr.poll_option_id || !pr.stake){ return }
            if(action_loading){ return }
            setMyData({ ...my_data, action_loading: true })
            //let new_pr = await PollResponseApi.createPollResponse(pr);
            let response = await PollResponseApi.createPMOrder(pr.poll_option_id, parseFloat(pr.stake as string))
            if(!response){ return setMyData({ ...my_data, action_loading:false }) }
            setMyData({
                ...my_data,
                poll_responses: poll_responses.filter(pr => pr.poll_response_id != response.poll_response.poll_response_id).concat(response.poll_response)
            })
        } catch (e) {
            alert('Unable to process!')
        }
    }

    const handleNext = async() => {
        if(!active_poll_id){ return }
        let active_index = active_polls.findIndex(p => p.poll_id == active_poll_id);
        let new_poll = active_polls.find((p,i) => i > active_index && ['active','closed'].includes(p.status));
        if(!new_poll){
            setModuleData({ ...module_data, active_poll_id: undefined })
            setFullLeaderboard(true);
            return
        }
        setModuleData({ ...module_data, active_poll_id: new_poll.poll_id })
    }

    const renderSections = (data:{ item:string, index:number }) => {
        switch(data.item){
            case 'header':
                if(!poll_campaign){ return <></> }
                return (
                    <CampaignHeader
                        onBack={onBack}
                        poll_campaign={poll_campaign}
                        onSharePollCampaign={onSharePollCampaign}
                    />
                )
            case 'progress': 
                if(!poll_campaign){ return <></> }
                return (
                    <View type='header' style={{ padding:10 }}>
                        <CampaignProgressBar
                            poll_campaign={poll_campaign}
                            polls={active_polls}
                            active_poll={active_poll_id ?? '0'}
                            onPollSelect={(p) => console.log(p)}
                            poll_responses={poll_responses}
                        />
                    </View>
                )
            case 'results':
                if(!poll_campaign){ return <></> }
                return (
                    <View style={{ padding:10 }}>
                        <CampaignResult
                            poll_campaign={poll_campaign}
                            onShowFullLeaderboard={() => setFullLeaderboard(true)}
                        />
                    </View>
                )
            case 'questions':
                if(!poll_campaign){ return <></> }
                return (
                    <View style={{ padding:10 }}>
                        <PollQuestionsSection
                            poll_campaign={poll_campaign}
                            polls={active_polls}
                            poll_responses={poll_responses}
                            onSelectPoll={(p) => {
                                if(!player_id){ return onRequestAuthenticate(poll_campaign.auth_strategy_id, poll_campaign.company_id) }
                                if(['paused','pending','inactive'].includes(p.status)){ return Alert.alert('This question is not active. Please open when it becomes active') }
                                if(poll_campaign.market_type == 'FOR_MONEY' && onFlashSelect){
                                    return onFlashSelect(p.poll_id)
                                }
                                setModuleData({ ...module_data, active_poll_id: p.poll_id })
                            }}
                        />
                    </View>
                )
            default: return <></>
        }
    }



    return (
        <View style={{ flex:1 }}>
            <FlatList
                data={sections}
                refreshing={loading}
                onRefresh={() => getDataFromServer()}
                key='poll_sections'
                keyExtractor={item => item}
                renderItem={renderSections}
            />
            <Modal
                style={{ flex:1 }}
                animationType={view_mode == 'desktop' ? 'fade' : 'slide'}
                transparent
                visible={active_poll_id ? true : false}
                >
                <View type='blur' style={view_mode=='desktop'?{flex:1, justifyContent:'center', alignItems:'center'}:{ flex:1, justifyContent:'flex-end', padding:12, paddingBottom:(insets?.bottom??0) + 12, paddingTop:(insets?.top??0) + 12 }}>
                    <PollCard
                        poll_id={active_poll_id}
                        action_loading={action_loading}
                        poll_response={active_response}
                        height={height}
                        width={view_mode == 'desktop' ? 500 : width}
                        view_mode={view_mode}
                        onSkip={() => handleNext()}
                        onCancel={() => setModuleData({ ...module_data, active_poll_id: undefined })}
                        onResponse={(response) => handleSubmitResponse(response)}
                        onShowAuthenticate={() => console.log('authenticate!!')}
                    />
                </View>
            </Modal>
            {full_leaderboard && poll_campaign ?
            <Modal
                transparent
                animationType={view_mode == 'desktop' ? 'fade' : 'slide'}
                visible={full_leaderboard?true:false}
                style={{ flex:1 }}
            >
                <View type='blur' style={view_mode=='desktop'?{flex:1, justifyContent:'center', alignItems:'center'}:{ flex:1, justifyContent:'flex-end', padding:12, paddingBottom:(insets?.bottom??0) + 12, paddingTop:(insets?.top??0) + 12 }}>
                        <CampaignLeaderboard
                            poll_campaign={poll_campaign}
                            view_mode='full'
                            mode={view_mode}
                            height={height}
                            onClose={() => setFullLeaderboard(false)}
                        />
                    </View>
            </Modal>
            :<></>}
            <SocketManager 
                onConnect={() => {
                    if(needs_reload){
                        setNeedsReload(false);
                        getDataFromServer();
                    }
                }}
                onDisconnect={() => setNeedsReload(true)}
                distinct_id={distinct_id}
                onSocketEvent={(ev) => {
                    switch(ev.type){
                        case 'UPDATE_POLL_CAMPAIGNS':
                            let pc = ev.poll_campaigns.find((pc:PollCampaignProps) => pc.poll_campaign_id == poll_campaign_id)
                            if(!pc){ return }
                            setSocketCampaign(pc)
                            break;
                        case 'UPDATE_POLLS':
                            if(ev.polls.length == 0){ return }
                            if(!ev.polls[0]){ return }
                            if(ev.polls[0].poll_campaign_id != poll_campaign_id){ return }//This is not our poll campaign!
                            setSocketPolls(ev.polls)
                            break
                        case 'UPDATE_POLL_OPTIONS':
                            if(ev.poll_options.length == 0){ return }
                            let existing_polls = polls.filter(p => ev.poll_options.map((o:PollOptionProps) => o.poll_id.toString()).includes(p.poll_id.toString()))
                            if(existing_polls.length == 0){ return } //No our poll!
                            //setSocketPollOptions(ev.poll_options)
                            break
                        default: break;
                    }
                }}
                subscribed_events={['UPDATE_POLL_CAMPAIGNS', 'UPDATE_POLLS','UPDATE_POLL_OPTIONS']}
            />
        </View>
    )
    

}

export default PollCampaign